import { SubPageName } from 'enums/buffet'
import {
  type BuffetProductListQuery,
  LineItemServiceEnum,
  LineItemServiceTypeForBuffetEnum,
  LineItemServiceTypeForDineInEnum,
  ProductAvailableServiceEnum,
  ProductServiceEnum,
} from 'graphQL/dinein/generate/operations'
import { getPropertyByLang } from 'helpers/i18next'
import { getResizableURL } from 'helpers/image'
import { BuffetPackageProducts } from 'hooks/buffet/useCategory'
import { i18n } from 'helpers/i18next'

export const generateBuffetProductDetailsUrl = ({
  categoryID,
  productID,
  serviceType,
}: {
  categoryID: number
  productID: number
  serviceType: LineItemServiceEnum | LineItemServiceTypeForDineInEnum | LineItemServiceTypeForBuffetEnum | undefined
}) => {
  const productDetailPath = `${SubPageName.ProductDetail}/${categoryID}/${productID}`
  const queryParams =
    serviceType === LineItemServiceEnum.BuffetALaCarteTakeaway
      ? `?product_service_type=${LineItemServiceEnum.BuffetALaCarteTakeaway}`
      : ''
  return `${productDetailPath}${queryParams}`
}

export const getAvailableService = ({
  productID,
  products,
  orderPackageID,
}: {
  productID: number
  products: {
    id: number
    buffetPackageProducts: { buffetPackageID: number }[]
    availableServices: ProductServiceEnum[]
  }[]
  orderPackageID
}) => {
  const findProduct = products.find((product) => product.id === productID)

  if (!findProduct) {
    return ProductAvailableServiceEnum.Buffet
  }

  const includePackage = findProduct.buffetPackageProducts.some(
    (buffetPackage) => buffetPackage.buffetPackageID === orderPackageID
  )
  if (includePackage) {
    return ProductAvailableServiceEnum.Buffet
  } else if (findProduct.availableServices.includes(ProductServiceEnum.BuffetALaCarte)) {
    return ProductAvailableServiceEnum.BuffetALaCarte
  } else if (findProduct.availableServices.includes(ProductServiceEnum.BuffetALaCarteTakeaway)) {
    return ProductAvailableServiceEnum.BuffetALaCarteTakeaway
  }
  return ProductAvailableServiceEnum.Buffet
}

interface Image {
  image1x: string
  image2x: string
  image3x: string
}

export interface ExcludeProduct {
  nameEn: string
  nameTh: string
  active: boolean
  id: number
  name: string
  label: string
  description: string
  image: Image
}

export const filterBuffetProductsByCategory = ({
  buffetProductList,
  categoryId,
  currentBuffetPackageId,
}: {
  buffetProductList: BuffetProductListQuery['listBuffetPackageProducts']['products']
  categoryId: number
  currentBuffetPackageId: number
}) => {
  const includedProducts: BuffetPackageProducts = []
  const excludedProducts: ExcludeProduct[] = []
  const aLaCarteProducts: BuffetPackageProducts = []

  buffetProductList.forEach((product) => {
    const inPackage = product.buffetPackageProducts.find((p) => p.buffetPackageID === currentBuffetPackageId)
    if (product.category.id === categoryId && product.active) {
      if (inPackage) {
        includedProducts.push(product)
      } else if (
        product.availableServices.includes(ProductServiceEnum.BuffetALaCarte) ||
        product.availableServices.includes(ProductServiceEnum.BuffetALaCarteTakeaway)
      ) {
        aLaCarteProducts.push(product)
      } else {
        const packageName = getPropertyByLang({
          th: product.buffetPackageProducts[0]?.buffetPackage.nameTh,
          en: product.buffetPackageProducts[0]?.buffetPackage.nameTh,
        })
        const image = {
          image1x: getResizableURL(product.images, { width: '250', height: '250', formatType: '_fmt-jpg' }),
          image2x: getResizableURL(product.images, { width: '330', height: '330', formatType: '_fmt-jpg' }),
          image3x: getResizableURL(product.images, { width: '375', height: '375', formatType: '_fmt-jpg' }),
        }

        excludedProducts.push({
          id: product.id,
          nameEn: product.nameEn,
          nameTh: product.nameTh,
          name: getPropertyByLang({ th: product.nameTh, en: product.nameEn }),
          active: product.active,
          label: i18n.t('buffet.productListPage.excluded'),
          description: packageName,
          image,
        })
      }
    }
  })

  return { includedProducts, excludedProducts, aLaCarteProducts }
}
